// src/components/billing/CheckoutStatusHandler.tsx

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useEventPaywall from '../../hooks/useEventPaywall';
import SparkCard from '../common/SparkCard';

interface Props {
  eventId: string;
  children: React.ReactNode;
}

const CheckoutStatusHandler: React.FC<Props> = ({ eventId, children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { unlocked, loading } = useEventPaywall(eventId);
  const [showPollStatus, setShowPollStatus] = useState(false);
  const [pollTimeout, setPollTimeout] = useState(false);

  const paymentStatus = searchParams.get('payment');

  useEffect(() => {
    if (paymentStatus === 'success') {
      setShowPollStatus(true);
      
      // Set timeout for polling (2 minutes)
      const timeout = setTimeout(() => {
        setPollTimeout(true);
      }, 120000); // 2 minutes

      // Clear URL params after starting poll
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('payment');
      setSearchParams(newParams, { replace: true });

      return () => clearTimeout(timeout);
    }
  }, [paymentStatus, searchParams, setSearchParams]);

  useEffect(() => {
    // If payment is unlocked, hide polling status
    if (unlocked && showPollStatus) {
      setShowPollStatus(false);
      setPollTimeout(false);
    }
  }, [unlocked, showPollStatus]);

  if (showPollStatus && !unlocked) {
    return (
      <SparkCard variant="solid" style={{ 
        position: 'fixed', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        width: '90%',
        maxWidth: '500px'
      }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          {!pollTimeout ? (
            <>
              <div className="spark-spinner" style={{ marginBottom: '1rem' }} />
              <h3 style={{ color: '#000', marginBottom: '1rem' }}>Processing Payment...</h3>
              <p style={{ color: '#666' }}>
                Your payment was successful! We're unlocking your event distribution now.
                This usually takes just a few seconds.
              </p>
            </>
          ) : (
            <>
              <h3 style={{ color: '#b00020', marginBottom: '1rem' }}>Taking Longer Than Expected</h3>
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                Your payment was processed, but unlocking is taking longer than usual.
                This can happen during high traffic periods.
              </p>
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                Please refresh the page in a few minutes, or contact support if the issue persists.
              </p>
              <button 
                onClick={() => window.location.reload()}
                style={{
                  background: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginRight: '1rem'
                }}
              >
                Refresh Page
              </button>
              <a 
                href="mailto:support@sparkinvites.com?subject=Payment%20Processing%20Delay"
                style={{
                  color: 'var(--color-primary)',
                  textDecoration: 'underline'
                }}
              >
                Contact Support
              </a>
            </>
          )}
        </div>
      </SparkCard>
    );
  }

  return <>{children}</>;
};

export default CheckoutStatusHandler;